import AiIcon from "../../components/Core/AiIcon/AiIcon";
import {
	Check,
	ChevronDown,
	ClipboardPaste,
	Code2,
	ChevronLeft,
	ChevronRight,
	Copy,
	Info,
	LayoutDashboard,
	Link2,
	Lock,
	MonitorPlay,
	Pencil,
	Plus,
	Redo2,
	Settings,
	Share2,
	StickyNote,
	Trash2,
	Undo2,
	Users,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
	DateRangePicker,
	FilterBar,
	NoteDialog,
	QUICK_PRESETS,
	REPORT_TEMPLATES,
	ReportPlanDialog,
	ReportRows,
	DashboardImportDialog,
	DashboardInfoDialog,
	ShareDialog,
	serializeDashboard,
	WidgetCard,
	WidgetDialog,
	afterMenuClose,
	describeDateRange,
	presetLabel,
	resolveDateRange,
	stepRange,
	templateWidgets,
	TvHeader,
	useShareSync,
	useTvCondensed,
	useTvFullscreen,
} from "../../components/Shared/ReportBuilder";
import { useAiAssistantEnabled } from "../../components/Shared/AiAssistant/AiAssistantTrigger";
import { useActiveDashboard, useReportBuilderStore } from "../../hooks/reportBuilder.hooks";
import { useDeleteDashboard, useSharedDashboard } from "../../networking/report-dashboards.hooks";
import { ReportDashboard, ReportWidget, widgetTitle } from "../../models/ReportBuilder";

/** Text/markdown widgets are built (store, card, share, public page) but hidden from the toolbar for now. */
const NOTES_ENABLED = false;

/**
 * Åtkomst och publik länk är oberoende sedan delning med namngivna personer
 * kom till, så etiketten visar båda när båda är på.
 */
const accessLabel = (dashboard: ReportDashboard): string => {
	const access =
		dashboard.visibility === "organisation"
			? "Alla"
			: dashboard.visibility === "users"
			? `${dashboard.sharedWith?.length ?? 0} personer`
			: "Privat";
	return dashboard.share ? `${access} + publik länk` : access;
};

const REFRESH_OPTIONS: { value: number | undefined; label: string }[] = [
	{ value: undefined, label: "Manuellt" },
	{ value: 1, label: "Varje minut" },
	{ value: 5, label: "Var 5:e minut" },
	{ value: 15, label: "Var 15:e minut" },
	{ value: 60, label: "Varje timme" },
];

/** Dashed "add a widget" tile: fills the page on an empty report, sits under the grid in edit mode. */
const AddWidgetTile: React.FC<{ size: "large" | "compact"; onClick: () => void }> = ({
	size,
	onClick,
}) => (
	<button
		type="button"
		onClick={onClick}
		className={cn(
			"group flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 transition-colors hover:border-brand hover:bg-brand/5 hover:text-brand",
			size === "large" ? "min-h-[320px] flex-1" : "min-h-[112px] shrink-0"
		)}
	>
		<span
			className={cn(
				"flex items-center justify-center rounded-full bg-gray-200 transition-colors group-hover:bg-brand/10",
				size === "large" ? "h-24 w-24" : "h-12 w-12"
			)}
		>
			<Plus className={size === "large" ? "h-14 w-14" : "h-7 w-7"} strokeWidth={1.5} />
		</span>
		{size === "large" ? (
			<>
				<span className="text-base font-medium">Lägg till din första widget</span>
				<span className="text-sm text-gray-400 group-hover:text-brand/70">
					Beskriv vad du vill se, till exempel omsättning per månad
				</span>
			</>
		) : (
			<span className="text-sm font-medium">Lägg till widget</span>
		)}
	</button>
);

const iconButton =
	"inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white";

/** Full-width rows of the empty state: one way to start a dashboard per row. */
const startOption =
	"flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left transition-colors";
const startOptionPrimary = "bg-brand text-white hover:bg-brand/90";
const startOptionSecondary =
	"border border-gray-300 bg-white text-gray-800 hover:border-brand hover:bg-brand/5";

/**
 * User-built report dashboards on top of the analytics lake. Widgets are
 * created from prompts (backend generates SQL + chart), share one date range
 * and one set of filters, and are stored per user on the server (see
 * `dashboardSync`). A dashboard a colleague shares opens here read-only.
 */
const ReportBuilderPage: React.FC = () => {
	const enabled = useAiAssistantEnabled();
	const store = useReportBuilderStore();
	const dashboard = useActiveDashboard();
	const [dialog, setDialog] = useState<{ open: boolean; widget?: ReportWidget; prompt?: string }>({
		open: false,
	});
	const [shareOpen, setShareOpen] = useState(false);
	const [planOpen, setPlanOpen] = useState(false);
	const [noteOpen, setNoteOpen] = useState(false);
	const [infoOpen, setInfoOpen] = useState(false);
	const [importOpen, setImportOpen] = useState(false);
	const [notice, setNotice] = useState<string>();
	const flash = (text: string) => {
		setNotice(text);
		window.setTimeout(() => setNotice(undefined), 2500);
	};
	const deleteDashboard = useDeleteDashboard();
	const tv = store.tvMode;
	const setTv = store.setTvMode;
	const [condensed, setCondensed] = useTvCondensed();
	/** A dashboard someone else owns is only in view here; the owner alone edits it. */
	const readOnly = !!dashboard?.owner;
	const editMode = store.editMode && !readOnly;
	const canUndo = store.past.length > 0;
	const canRedo = store.future.length > 0;

	const range = useMemo(
		() => (dashboard ? resolveDateRange(dashboard.range) : { from: "", to: "" }),
		[dashboard]
	);
	useShareSync(dashboard);

	// Deep links from the side menu: ?report=<id> opens one of the user's own,
	// ?shared=<id> fetches a colleague's and opens it read-only. New dashboards
	// are created by the side menu itself and land here already active.
	const [searchParams, setSearchParams] = useSearchParams();
	const [sharedId, setSharedId] = useState<string>();
	const sharedQuery = useSharedDashboard(sharedId);

	useEffect(() => {
		if (!enabled) return;
		const reportId = searchParams.get("report");
		const openShared = searchParams.get("shared");
		if (!reportId && !openShared) return;
		if (reportId && store.dashboards.some((d) => d.id === reportId)) {
			store.setActiveDashboard(reportId);
		}
		if (openShared) setSharedId(openShared);
		setSearchParams({}, { replace: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, searchParams]);

	useEffect(() => {
		if (sharedQuery.data) store.openSharedDashboard(sharedQuery.data);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sharedQuery.data]);

	// Ctrl/Cmd+Z and Shift+Ctrl/Cmd+Z while editing, unless typing in a field.
	useEffect(() => {
		if (!editMode) return;
		const onKey = (e: KeyboardEvent) => {
			const target = e.target as HTMLElement | null;
			if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
			if (target?.isContentEditable) return;
			if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "z") return;
			e.preventDefault();
			e.shiftKey ? store.redo() : store.undo();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [editMode, store]);

	useEffect(() => () => setTv(false), [setTv]);

	useTvFullscreen(tv, setTv);

	if (!enabled)
		return (
			<div className="p-6 text-sm text-gray-600">
				Dashboards är inte aktiverat för din organisation.
			</div>
		);

	const createDashboard = () => {
		const name = window.prompt("Namn på dashboarden", `Dashboard ${store.dashboards.length + 1}`);
		if (name) store.createDashboard(name.trim());
	};

	const rename = () => {
		if (!dashboard) return;
		const name = window.prompt("Nytt namn", dashboard.name);
		if (name?.trim()) store.renameDashboard(dashboard.id, name.trim());
	};

	const openNewWidget = () => {
		store.setEditMode(true);
		setDialog({ open: true });
	};

	const remove = () => {
		if (!dashboard) return;
		// Deleting is the whole thing, not just this browser's copy: there is one
		// stored dashboard, and the backend withdraws its public link along with it.
		const also = [
			dashboard.visibility === "organisation" || dashboard.visibility === "users"
				? "Den försvinner även för kollegorna du delat den med."
				: undefined,
			dashboard.share ? "Den publika länken slutar fungera." : undefined,
		].filter(Boolean);
		const question = [`Ta bort dashboarden "${dashboard.name}"?`, ...also].join(" ");
		if (!window.confirm(question)) return;
		deleteDashboard.mutate({ dashboardId: dashboard.id });
		store.deleteDashboard(dashboard.id);
	};

	/** A colleague's dashboard becomes the user's own, private copy. */
	const copyToOwn = () => dashboard && store.duplicateDashboard(dashboard.id);

	const otherDashboards = store.dashboards
		.filter((d) => d.id !== dashboard?.id)
		.map((d) => ({ id: d.id, name: d.name }));

	return (
		<div
			className={cn(
				"report-builder flex min-h-full flex-col gap-4 p-6 pb-10",
				// TV mode fills the viewport exactly and never scrolls; the rows squeeze to fit.
				tv && "h-screen overflow-hidden bg-gray-100 pb-6",
				tv && condensed && "gap-3 p-4"
			)}
		>
			<div className={cn("flex flex-wrap items-center justify-between gap-3", tv && "hidden")}>
				<div className="min-w-0">
					{dashboard && (
						<p className="text-xs font-medium uppercase tracking-wide text-gray-500">Dashboards</p>
					)}
					{dashboard ? (
						<div className="flex items-center gap-2">
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<button
										type="button"
										className="inline-flex max-w-[28rem] items-center gap-1 rounded-md py-0.5 pr-1 text-left hover:bg-gray-100"
									>
										<h1 className="truncate text-xl font-semibold text-gray-900">
											{dashboard.name}
										</h1>
										<ChevronDown className="h-4 w-4 shrink-0 text-gray-500" />
									</button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="start" className="z-[1300] w-64">
									<DropdownMenuRadioGroup
										value={dashboard.id}
										onValueChange={(id) => store.setActiveDashboard(id)}
									>
										{store.dashboards.map((d) => (
											<DropdownMenuRadioItem key={d.id} value={d.id}>
												<span className="truncate">{d.name}</span>
												{/* Who it belongs to, kept to the quiet end of the row: a
												    colleague's name, or a lock on one's own private ones. */}
												{d.owner ? (
													d.owner.name && (
														<span className="ml-auto shrink-0 pl-3 text-xs text-gray-500">
															{d.owner.name}
														</span>
													)
												) : (d.visibility ?? "private") === "private" ? (
													<span className="ml-auto shrink-0 pl-3" title="Privat – bara du ser den">
														<Lock className="h-3 w-3 text-gray-400" aria-hidden />
													</span>
												) : null}
											</DropdownMenuRadioItem>
										))}
									</DropdownMenuRadioGroup>
									<DropdownMenuSeparator />
									<DropdownMenuItem onSelect={() => afterMenuClose(createDashboard)}>
										<Plus className="mr-2 h-4 w-4" /> Ny dashboard…
									</DropdownMenuItem>
									<DropdownMenuItem onSelect={() => afterMenuClose(() => setPlanOpen(true))}>
										<AiIcon className="mr-2 h-4 w-4" /> Ny från beskrivning…
									</DropdownMenuItem>
									<DropdownMenuSub>
										<DropdownMenuSubTrigger>
											<LayoutDashboard className="mr-2 h-4 w-4" /> Ny från mall
										</DropdownMenuSubTrigger>
										<DropdownMenuSubContent className="z-[1300]">
											{REPORT_TEMPLATES.map((template) => (
												<DropdownMenuItem
													key={template.name}
													onSelect={() => store.createFromTemplate(template)}
												>
													{template.name}
												</DropdownMenuItem>
											))}
										</DropdownMenuSubContent>
									</DropdownMenuSub>
									<DropdownMenuItem onSelect={() => store.duplicateDashboard(dashboard.id)}>
										<Copy className="mr-2 h-4 w-4" />
										{readOnly ? "Spara som min egen" : "Duplicera dashboard"}
									</DropdownMenuItem>
									{!readOnly && (
										<>
											<DropdownMenuItem onSelect={() => afterMenuClose(rename)}>
												<Pencil className="mr-2 h-4 w-4" /> Byt namn…
											</DropdownMenuItem>
											<DropdownMenuItem
												onSelect={() => afterMenuClose(remove)}
												className="text-danger focus:text-danger"
											>
												<Trash2 className="mr-2 h-4 w-4" /> Ta bort dashboard
											</DropdownMenuItem>
										</>
									)}
								</DropdownMenuContent>
							</DropdownMenu>
							{readOnly && (
								<span
									className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600"
									title="Du kan läsa den här dashboarden, men bara ägaren kan ändra den"
								>
									<Users className="h-3 w-3" />
									{dashboard.owner?.name ? `Delad av ${dashboard.owner.name}` : "Delad med dig"}
								</span>
							)}
							{dashboard.share && !readOnly && (
								<button
									type="button"
									onClick={() => setShareOpen(true)}
									className="inline-flex items-center gap-1 rounded-full bg-action/10 px-2 py-0.5 text-xs font-medium text-action"
									title={
										dashboard.share.hasPin
											? "Delad via publik länk med PIN"
											: "Delad via publik länk"
									}
								>
									<Link2 className="h-3 w-3" /> Delad
								</button>
							)}
						</div>
					) : (
						<h1 className="text-xl font-semibold text-gray-900">Dashboards</h1>
					)}
				</div>
				{dashboard && (
					<div className="flex flex-wrap items-center gap-2">
						<div className="flex items-center gap-1">
							<button
								type="button"
								onClick={() => store.setRange(dashboard.id, stepRange(dashboard.range, -1))}
								className={iconButton}
								title="Föregående period"
								aria-label="Föregående period"
							>
								<ChevronLeft className="h-4 w-4" />
							</button>
							<DateRangePicker
								range={dashboard.range}
								compare={dashboard.compare}
								onApply={(r, c) => {
									store.setRange(dashboard.id, r);
									store.setCompare(dashboard.id, c);
								}}
							/>
							<button
								type="button"
								onClick={() => store.setRange(dashboard.id, stepRange(dashboard.range, 1))}
								className={iconButton}
								title="Nästa period"
								aria-label="Nästa period"
							>
								<ChevronRight className="h-4 w-4" />
							</button>
						</div>
						{editMode && (
							<div className="flex items-center gap-1">
								<button
									type="button"
									onClick={() => store.undo()}
									disabled={!canUndo}
									className={iconButton}
									title="Ångra (Ctrl+Z)"
									aria-label="Ångra"
								>
									<Undo2 className="h-4 w-4" />
								</button>
								<button
									type="button"
									onClick={() => store.redo()}
									disabled={!canRedo}
									className={iconButton}
									title="Gör om (Shift+Ctrl+Z)"
									aria-label="Gör om"
								>
									<Redo2 className="h-4 w-4" />
								</button>
							</div>
						)}
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<button
									type="button"
									className={iconButton}
									title="Inställningar för dashboarden"
									aria-label="Inställningar för dashboarden"
								>
									<Settings className="h-4 w-4" />
								</button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" className="z-[1300] w-60">
								<DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-gray-400">
									Automatisk uppdatering
								</DropdownMenuLabel>
								<DropdownMenuRadioGroup
									value={String(dashboard.autoRefreshMinutes ?? "")}
									onValueChange={(v) =>
										store.setAutoRefresh(dashboard.id, v ? Number(v) : undefined)
									}
								>
									{REFRESH_OPTIONS.map((o) => (
										<DropdownMenuRadioItem key={o.label} value={String(o.value ?? "")}>
											{o.label}
										</DropdownMenuRadioItem>
									))}
								</DropdownMenuRadioGroup>
								<DropdownMenuSeparator />
								{!readOnly && (
									<DropdownMenuItem onSelect={() => afterMenuClose(() => setShareOpen(true))}>
										<Share2 className="mr-2 h-4 w-4" /> Delning…
										<span className="ml-auto text-xs text-gray-500">{accessLabel(dashboard)}</span>
									</DropdownMenuItem>
								)}
								<DropdownMenuItem
									onSelect={() => {
										store.setEditMode(false);
										setTv(true);
									}}
								>
									<MonitorPlay className="mr-2 h-4 w-4" /> TV-läge (helskärm)
								</DropdownMenuItem>
								<DropdownMenuItem onSelect={() => afterMenuClose(() => setInfoOpen(true))}>
									<Info className="mr-2 h-4 w-4" /> Information…
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuSub>
									<DropdownMenuSubTrigger>
										<Code2 className="mr-2 h-4 w-4" /> Avancerat
									</DropdownMenuSubTrigger>
									<DropdownMenuSubContent className="z-[1300]">
										<DropdownMenuItem
											onSelect={() =>
												navigator.clipboard
													.writeText(serializeDashboard(dashboard))
													.then(() => flash("Dashboarden kopierad som JSON"))
													.catch((e: Error) => flash(e.message))
											}
										>
											<Copy className="mr-2 h-4 w-4" /> Kopiera som JSON
										</DropdownMenuItem>
										<DropdownMenuItem onSelect={() => afterMenuClose(() => setImportOpen(true))}>
											<ClipboardPaste className="mr-2 h-4 w-4" /> Klistra in från JSON…
										</DropdownMenuItem>
									</DropdownMenuSubContent>
								</DropdownMenuSub>
							</DropdownMenuContent>
						</DropdownMenu>
						{notice && (
							<span className="self-center text-xs text-brand" role="status">
								{notice}
							</span>
						)}
						{editMode ? (
							<>
								{NOTES_ENABLED && (
									<button
										type="button"
										onClick={() => setNoteOpen(true)}
										className="inline-flex h-9 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-800 hover:bg-gray-50"
										title="Rubrik eller text mellan widgets"
									>
										<StickyNote className="h-4 w-4" /> Text
									</button>
								)}
								<button
									type="button"
									onClick={() => setDialog({ open: true })}
									className="inline-flex h-9 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-800 hover:bg-gray-50"
								>
									<Plus className="h-4 w-4" /> Ny widget
								</button>
								<button
									type="button"
									onClick={() => store.setEditMode(false)}
									className="inline-flex h-9 items-center gap-1.5 rounded-md bg-gray-900 px-3 text-sm font-medium text-white"
								>
									<Check className="h-4 w-4" /> Klar
								</button>
							</>
						) : readOnly ? (
							<button
								type="button"
								onClick={copyToOwn}
								className="inline-flex h-9 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-800 hover:bg-gray-50"
								title="Skapa en egen kopia som du kan ändra"
							>
								<Copy className="h-4 w-4" /> Spara som min egen
							</button>
						) : (
							<button
								type="button"
								onClick={() => store.setEditMode(true)}
								className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand px-3 text-sm font-medium text-white"
							>
								<Pencil className="h-4 w-4" /> Redigera
							</button>
						)}
					</div>
				)}
			</div>

			{dashboard && !tv && (
				<div className="flex flex-wrap items-center gap-3">
					<div className="flex flex-wrap items-center gap-1">
						{QUICK_PRESETS.map((p) => (
							<button
								key={p}
								type="button"
								onClick={() => store.setRange(dashboard.id, { preset: p })}
								className={cn(
									"rounded-full border px-2.5 py-0.5 text-xs font-medium",
									dashboard.range.preset === p
										? "border-gray-900 bg-gray-900 text-white"
										: "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
								)}
							>
								{presetLabel(p)}
							</button>
						))}
					</div>
					<FilterBar
						filters={dashboard.filters}
						editable={editMode}
						onChange={(f) => store.setFilters(dashboard.id, f)}
					/>
				</div>
			)}

			{dashboard && tv && (
				<TvHeader
					name={dashboard.name}
					meta={describeDateRange(dashboard.range)}
					condensed={condensed}
					onCondensed={setCondensed}
					onExit={() => setTv(false)}
				/>
			)}

			{!dashboard && (
				<div className="flex flex-1 flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 p-10">
					<div className="w-full max-w-lg">
						<h2 className="text-center text-lg font-semibold text-gray-900">
							Ingen dashboard ännu
						</h2>
						<p className="mt-1 text-center text-sm text-gray-600">
							Börja på något av sätten nedan och lägg till widgets med en fråga i klartext.
						</p>
						<div className="mt-7 flex flex-col gap-3">
							<button
								type="button"
								onClick={createDashboard}
								className={cn(startOption, startOptionPrimary)}
							>
								<Plus className="h-5 w-5 shrink-0" />
								<span className="min-w-0">
									<span className="block text-sm font-semibold">Skapa tom dashboard</span>
									<span className="block text-xs text-white/80">
										Börja från en tom yta och lägg till widgets en och en.
									</span>
								</span>
							</button>
							<button
								type="button"
								onClick={() => setPlanOpen(true)}
								className={cn(startOption, startOptionSecondary)}
							>
								<AiIcon className="h-5 w-5 shrink-0" />
								<span className="min-w-0">
									<span className="block text-sm font-semibold">Bygg från beskrivning</span>
									<span className="block text-xs text-gray-500">
										Beskriv vad du vill följa, så föreslås widgets att godkänna.
									</span>
								</span>
							</button>
							{REPORT_TEMPLATES.map((template) => (
								<button
									key={template.name}
									type="button"
									onClick={() => store.createFromTemplate(template)}
									className={cn(startOption, startOptionSecondary)}
								>
									<LayoutDashboard className="h-5 w-5 shrink-0" />
									<span className="min-w-0">
										<span className="block text-sm font-semibold">
											Skapa från mall: {template.name}
										</span>
										<span className="block text-xs text-gray-500">
											{template.description
												? `${template.description} `
												: `${templateWidgets(template).length} färdiga widgets. `}
											{describeDateRange(template.range)}.
										</span>
									</span>
								</button>
							))}
						</div>
					</div>
				</div>
			)}

			{dashboard && dashboard.widgets.length === 0 && (
				<AddWidgetTile size="large" onClick={openNewWidget} />
			)}

			{dashboard && dashboard.widgets.length > 0 && (
				<ReportRows
					rows={dashboard.rows}
					widgets={dashboard.widgets}
					editable={editMode && !tv}
					fit={tv}
					condensed={condensed}
					onDrop={(target) => store.dropWidget(dashboard.id, target)}
					dragLabel={(id) => {
						const widget = dashboard.widgets.find((w) => w.id === id);
						return widget ? widgetTitle(widget).title : "";
					}}
					renderWidget={(id) => {
						const widget = dashboard.widgets.find((w) => w.id === id)!;
						return (
							<WidgetCard
								dashboard={dashboard}
								widget={widget}
								range={range}
								compare={dashboard.compare}
								filters={dashboard.filters}
								editable={editMode && !tv}
								otherDashboards={otherDashboards}
								onEditPrompt={(prompt) => setDialog({ open: true, widget, prompt })}
							/>
						);
					}}
				/>
			)}

			{dashboard && editMode && !tv && dashboard.widgets.length > 0 && (
				<AddWidgetTile size="compact" onClick={openNewWidget} />
			)}

			{dashboard && (
				<ShareDialog open={shareOpen} onOpenChange={setShareOpen} dashboard={dashboard} />
			)}

			{dashboard && (
				<DashboardInfoDialog open={infoOpen} onOpenChange={setInfoOpen} dashboard={dashboard} />
			)}

			<DashboardImportDialog
				open={importOpen}
				onOpenChange={setImportOpen}
				onImport={(data) => {
					store.importDashboard(data);
					flash("Dashboarden skapad");
				}}
			/>

			{dashboard && (
				<WidgetDialog
					open={dialog.open}
					onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
					widget={dialog.widget}
					initialPrompt={dialog.prompt}
					range={range}
					compare={dashboard.compare}
					filters={dashboard.filters}
					onSave={(spec, prompt) => {
						if (dialog.widget) store.regenerateWidget(dashboard.id, dialog.widget.id, spec, prompt);
						else store.addWidget(dashboard.id, { spec, prompt });
						setDialog({ open: false });
					}}
				/>
			)}

			{dashboard && NOTES_ENABLED && (
				<NoteDialog
					open={noteOpen}
					onOpenChange={setNoteOpen}
					onSave={(text) => store.addNote(dashboard.id, text)}
				/>
			)}
			<ReportPlanDialog open={planOpen} onOpenChange={setPlanOpen} />
		</div>
	);
};

export default ReportBuilderPage;
